/**
 * 聊天页数据编排 hook（story AC-6/7/8）。串起：
 *  - 单条 WebSocket 连接（ImConnection，页面生命周期内复用，内部自愈重连）
 *  - 历史加载（REST 分页，切会话重载）
 *  - 乐观 UI / ACK 归并 / 5s 超时 / 去重（chatReducer）
 * 一个连接服务所有会话；入站 MESSAGE 按当前 conversationId 过滤。
 */
import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useAuthStore } from '@/store/auth';
import { loadMessages } from '@/api/im';
import { ImConnection, type ImConnectionState } from './connection';
import { refreshImToken } from './tokenRefresh';
import { generateClientMsgId } from '@/lib/clientMsgId';
import { CHAT_CONTENT_MAX_LENGTH, type ImFrame, type SyncMessage } from './frame';
import {
  chatReducer,
  initialChatState,
  createOutgoingBubble,
  inboundBubbleFromFrame,
  isRetryable,
  type ChatBubble,
} from './chatMessages';

const ACK_TIMEOUT_MS = 5000;

export type HistoryStatus = 'idle' | 'loading' | 'error' | 'ready';

export interface UseChatResult {
  bubbles: ChatBubble[];
  dismissedBanners: string[];
  connState: ImConnectionState;
  historyStatus: HistoryStatus;
  currentUserId: string;
  canSend: boolean;
  send: (content: string) => void;
  retry: (bubble: ChatBubble) => void;
  dismissBanner: (clientMsgId: string) => void;
  reloadHistory: () => void;
}

export function useChat(conversationId: string): UseChatResult {
  const [state, dispatch] = useReducer(chatReducer, initialChatState);
  const [connState, setConnState] = useState<ImConnectionState>('DISCONNECTED');
  const [historyStatus, setHistoryStatus] = useState<HistoryStatus>('idle');
  const currentUserId = useAuthStore((s) => s.userId) ?? '';

  const connRef = useRef<ImConnection | null>(null);
  const convIdRef = useRef(conversationId);
  convIdRef.current = conversationId;
  const timeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const clearAckTimeout = useCallback((clientMsgId: string) => {
    const t = timeoutsRef.current.get(clientMsgId);
    if (t) {
      clearTimeout(t);
      timeoutsRef.current.delete(clientMsgId);
    }
  }, []);

  const armAckTimeout = useCallback(
    (clientMsgId: string) => {
      clearAckTimeout(clientMsgId);
      const t = setTimeout(() => {
        timeoutsRef.current.delete(clientMsgId);
        dispatch({ type: 'TIMEOUT', clientMsgId });
      }, ACK_TIMEOUT_MS);
      timeoutsRef.current.set(clientMsgId, t);
    },
    [clearAckTimeout],
  );

  // 帧处理：用 ref 让长连接始终调用到最新闭包。
  const handleFrame = useCallback(
    (frame: ImFrame) => {
      const convId = convIdRef.current;
      switch (frame.type) {
        case 'ACK':
          if (frame.clientMsgId) clearAckTimeout(frame.clientMsgId);
          dispatch({ type: 'ACK', frame });
          return;
        case 'ERROR':
          dispatch({ type: 'ERROR', code: frame.code ?? 'SEND_FAILED' });
          return;
        case 'MESSAGE':
          // 入站需 serverMsgId(真实帧必带):去重键靠它,缺失会导致 'peer-' 键碰撞误判重复。
          if (frame.conversationId === convId && frame.serverMsgId && frame.senderId && frame.content) {
            dispatch({ type: 'INBOUND', bubble: inboundBubbleFromFrame(frame) });
          }
          return;
        case 'SYNC_BATCH':
          (frame.messages ?? []).forEach((m: SyncMessage) => {
            if (m.conversationId === convId && m.serverMsgId && m.senderId && m.content) {
              dispatch({
                type: 'INBOUND',
                bubble: inboundBubbleFromFrame({ type: 'MESSAGE', ...m }),
              });
            }
          });
          return;
        default:
          return;
      }
    },
    [clearAckTimeout],
  );
  const handlerRef = useRef(handleFrame);
  handlerRef.current = handleFrame;

  // 建立单条连接（页面生命周期内一次）。
  useEffect(() => {
    const conn = new ImConnection({
      getCredentials: () => {
        const { userId, accessToken } = useAuthStore.getState();
        return { userId, token: accessToken };
      },
      refreshToken: refreshImToken,
      onState: setConnState,
      onFrame: (f) => handlerRef.current(f),
    });
    connRef.current = conn;
    conn.connect();
    const timeouts = timeoutsRef.current;
    return () => {
      conn.disconnect();
      connRef.current = null;
      timeouts.forEach((t) => clearTimeout(t));
      timeouts.clear();
    };
  }, []);

  const reloadHistory = useCallback(() => {
    let cancelled = false;
    setHistoryStatus('loading');
    loadMessages(conversationId)
      .then((resp) => {
        if (cancelled) return;
        dispatch({ type: 'LOAD_HISTORY', items: resp.items ?? [] });
        setHistoryStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setHistoryStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [conversationId]);

  // 切会话 → 重载历史。
  useEffect(() => {
    const cancel = reloadHistory();
    return cancel;
  }, [reloadHistory]);

  const doSend = useCallback(
    (clientMsgId: string, content: string) => {
      const ok = connRef.current?.sendChat(clientMsgId, convIdRef.current, content) ?? false;
      if (ok) {
        armAckTimeout(clientMsgId);
      } else {
        // 未连接/写失败：立即标记失败（可重试）。
        dispatch({ type: 'TIMEOUT', clientMsgId });
      }
    },
    [armAckTimeout],
  );

  const send = useCallback(
    (raw: string) => {
      const content = raw.trim().slice(0, CHAT_CONTENT_MAX_LENGTH);
      if (!content) return;
      if (connState !== 'CONNECTED') return;
      const clientMsgId = generateClientMsgId();
      dispatch({
        type: 'SEND',
        bubble: createOutgoingBubble({
          clientMsgId,
          conversationId: convIdRef.current,
          senderId: currentUserId,
          content,
          sentAtMs: Date.now(),
        }),
      });
      doSend(clientMsgId, content);
    },
    [connState, currentUserId, doSend],
  );

  const retry = useCallback(
    (bubble: ChatBubble) => {
      if (!isRetryable(bubble) || connState !== 'CONNECTED') return;
      dispatch({ type: 'RETRY', clientMsgId: bubble.clientMsgId });
      doSend(bubble.clientMsgId, bubble.content);
    },
    [connState, doSend],
  );

  const dismissBanner = useCallback((clientMsgId: string) => {
    dispatch({ type: 'DISMISS_BANNER', clientMsgId });
  }, []);

  const canSend = connState === 'CONNECTED';

  return {
    bubbles: state.bubbles,
    dismissedBanners: state.dismissedBanners,
    connState,
    historyStatus,
    currentUserId,
    canSend,
    send,
    retry,
    dismissBanner,
    reloadHistory,
  };
}
